import { Splide } from '@splidejs/splide';
import { AutoScroll } from '@splidejs/splide-extension-auto-scroll';

/**
 * Splide 初期化処理（メイン＆サムネイル）
 */
const initializeMainAndThumbSliders = () => {
  const thumbSelector = '#visual .splide-thumb';
  const mainSelector = '#visual .splide-main';

  if (document.querySelector(thumbSelector) && document.querySelector(mainSelector)) {
    const thumbOptions = {
      mediaQuery: 'min',
      lazyLoad: 'nearby',
      gap: 0,
      arrows: false,
      drag: false,
      pagination: false,
      isNavigation: true,
      direction: 'ttb',
      heightRatio: 1,
      perPage: 4,
      breakpoints: {
        600: {
          gap: 8,
          heightRatio: 1.2,
        },
        991: {
          gap: 8,
          heightRatio: 1.2,
        },
        1200: {
          direction: 'ltr',
          fixedHeight: '200px',
          gap: 16,
        },
      },
    };

    const thumbSplide = new Splide(thumbSelector, thumbOptions);

    // 初期化後に不要な role を削除
    const observer = new MutationObserver(() => {
      const splideList = document.querySelector(`${thumbSelector} .splide__list`);
      if (splideList && splideList.hasAttribute('aria-orientation')) {
        splideList.removeAttribute('aria-orientation');
        splideList.removeAttribute('role');
      }
    });

    thumbSplide.on('mounted', () => {
      const splideList = document.querySelector(`${thumbSelector} .splide__list`);
      if (splideList) {
        observer.observe(splideList, { attributes: true });
      }
    });

    const mainOptions = {
      lazyLoad: 'nearby',
      type: 'loop',
      autoplay: true,
      updateOnMove: true,
      speed: 2000,
      pagination: false,
      arrows: false,
      drag: false,
      height: 800,
      breakpoints: {
        1024: {
          height: 'clamp(400px,70vh, 600px)',
        },
      },
    };

    const mainSplide = new Splide(mainSelector, mainOptions);

    // サムネイルとメインを同期
    mainSplide.sync(thumbSplide);

    // Splide インスタンスをマウント
    mainSplide.mount();
    thumbSplide.mount();
  }
};

/**
 * Splide 初期化処理（メイン＆タイマー）
 */
const initializeMainAndTimer = () => {
  const mainSelector = '#visual.c-visual-timer';

  if (document.querySelector(mainSelector)) {
    const myDelay = 5000;

    const slide = document.querySelectorAll('#visual.c-visual-timer .splide__slide');
    const slideLength = slide.length;

    const fraction = document.querySelectorAll('#visual.c-visual-timer .slide-fraction');
    const fractionEl =
      '<span class="circle"><svg viewBox="0 0 64 64"><circle class="circle-01" cx="50%" cy="50%" r="31"></circle><circle class="circle-02" cx="50%" cy="50%" r="31"></circle></svg></span><span class="current"></span><span class="total">' +
      slideLength +
      '</span>';
    for (let i = 0; i < fraction.length; i++) {
      fraction[i].insertAdjacentHTML('beforeend', fractionEl);
    }

    const slideDelay = [];
    for (let i = 0; i < slideLength; i++) {
      let delay = Number(slide[i].getAttribute('data-splide-interval'));
      slideDelay[i] = delay ? delay : myDelay;
      slide[i].querySelector('img').style.transitionDuration = slideDelay[i] + 'ms';
      slide[i].querySelector('.circle-02').style.animationDuration = slideDelay[i] + 'ms';
      slide[i].querySelector('.current').textContent = i + 1;
    }

    const progressBar = document.querySelector('#visual.c-visual-timer .progressbar-fill');
    const updateProgress = (index) => {
      let start = Math.floor((index / slideLength) * 100) / 100;
      let end = Math.floor(((index + 1) / slideLength) * 100) / 100;

      progressBar.style.transition = '';
      progressBar.style.transform = 'scaleX(' + start + ')';
      setTimeout(() => {
        progressBar.style.transition = slideDelay[index] + 'ms linear';
        progressBar.style.transform = 'scaleX(' + end + ')';
      }, 100);
    };

    const target = '#visual.c-visual-timer .splide';
    const options = {
      type: 'fade',
      rewind: true,
      autoplay: true,
      speed: 1000,
      interval: myDelay,
      arrows: false,
      pagination: false,
      pauseOnHover: false, // マウスオーバーしたときに自動再生を止めるかどうか(false：止めない)
      pauseOnFocus: false, //スライダー内にフォーカスされたエレメントがある場合、自動再生を止めるかどうか。推奨はtrue。
    };
    const mainSplide = new Splide(target, options);

    // バーの幅を更新する
    mainSplide.on('mounted', function () {
      updateProgress(0);
    });
    mainSplide.on('move', function () {
      updateProgress(mainSplide.index);
    });

    mainSplide.mount();
  }
};

/**
 * ティッカースライダーの初期化
 */
const initializeTickerSlider = () => {
  const tickerSelector = '.js-ticker .splide';

  if (document.querySelector(tickerSelector)) {
    const tickerOptions = {
      lazyLoad: 'nearby',
      mediaQuery: 'min',
      fixedWidth: '15rem',
      gap: 16,
      type: 'loop',
      arrows: false,
      drag: 'free',
      flickPower: 300,
      pagination: false,
      autoScroll: {
        speed: 0.5,
        pauseOnHover: false,
        pauseOnFocus: false,
      },
      breakpoints: {
        1025: {
          gap: 32,
          fixedWidth: '15rem',
        },
      },
    };

    const tickerSplide = new Splide(tickerSelector, tickerOptions);

    // 初期化後に不要な role を削除
    const observer = new MutationObserver(() => {
      const splideList = document.querySelector(`${tickerSelector} .splide__list`);
      if (splideList && splideList.hasAttribute('aria-roledescription')) {
        splideList.removeAttribute('aria-roledescription');
        splideList.removeAttribute('role');
      }
    });

    tickerSplide.on('mounted', () => {
      const splideList = document.querySelector(`${tickerSelector} .splide__list`);
      if (splideList) {
        observer.observe(splideList, { attributes: true });
      }
    });

    // Splide Extensions を使用してマウント
    tickerSplide.mount({ AutoScroll });
  }
};

/**
 * 通常スライダーの初期化
 */
const initializeSlider = () => {
  const sliderSelector = '.js-slider .splide';

  if (document.querySelector(sliderSelector)) {
    const sliderOptions = {
      type: 'fade',
      rewind: true,
    };

    const sliderSplide = new Splide(sliderSelector, sliderOptions);

    // 初期化後に不要な role を削除
    const observer = new MutationObserver(() => {
      const splideList = document.querySelector(`${sliderSelector} .splide__list`);
      if (splideList && splideList.hasAttribute('aria-roledescription')) {
        splideList.removeAttribute('aria-roledescription');
        splideList.removeAttribute('role');
      }
    });

    sliderSplide.on('mounted', () => {
      const splideList = document.querySelector(`${sliderSelector} .splide__list`);
      if (splideList) {
        observer.observe(splideList, { attributes: true });
      }
    });

    // Splide Extensions を使用してマウント
    sliderSplide.mount();
  }
};

/**
 * Splide 全体の初期化
 */
const initializeSplide = () => {
  // TOPページのみで初期化
  if (window.location.pathname === '/' || window.location.pathname === '/renew/') {
    // initializeMainAndThumbSliders();
    initializeMainAndTimer();
  }
  initializeTickerSlider();
  initializeSlider();
};

/**
 * モジュールの初期化
 */
const init = () => {
  initializeSplide();
};

export default { init };
